import { useEffect } from 'react'
import { useUserStore } from '@/entities/user'
import { ApiError, queryClient } from '@/shared/api'
import { env } from '@/shared/config'
import { broadcastAuthEvent } from '@/shared/lib/auth-sync'
import { authApi } from '../api'

const STREAM_PATH = '/api/v1/catalog/stream'
const POLL_INTERVAL_MS = 45_000
const RECONNECT_DELAY_MS = 2_000

type CatalogAccessChangedEvent = {
  type: 'catalog_access_changed'
  userId: string
  accessVersion: string
  reason: string
  occurredAt: string
}

type BillingChangedEvent = {
  type: 'billing_changed'
  userId: string
  billingVersion: string
  reason: string
  occurredAt: string
}

const invalidateCatalogQueries = async () => {
  await queryClient.invalidateQueries({
    queryKey: ['catalog'],
    refetchType: 'active',
  })
}

const invalidateBillingQueries = async () => {
  await queryClient.invalidateQueries({
    queryKey: ['billing'],
    refetchType: 'active',
  })
}

const buildStreamUrl = () => {
  const apiBaseUrl = env.apiUrl?.replace(/\/+$/, '') ?? ''
  return apiBaseUrl.length > 0 ? `${apiBaseUrl}${STREAM_PATH}` : STREAM_PATH
}

const parseCatalogAccessChangedEvent = (value: string): CatalogAccessChangedEvent | null => {
  try {
    const parsed = JSON.parse(value) as Partial<CatalogAccessChangedEvent>
    if (
      parsed.type !== 'catalog_access_changed' ||
      typeof parsed.userId !== 'string' ||
      typeof parsed.accessVersion !== 'string' ||
      typeof parsed.reason !== 'string' ||
      typeof parsed.occurredAt !== 'string'
    ) {
      return null
    }

    return parsed as CatalogAccessChangedEvent
  } catch {
    return null
  }
}

const parseBillingChangedEvent = (value: string): BillingChangedEvent | null => {
  try {
    const parsed = JSON.parse(value) as Partial<BillingChangedEvent>
    if (
      parsed.type !== 'billing_changed' ||
      typeof parsed.userId !== 'string' ||
      typeof parsed.billingVersion !== 'string' ||
      typeof parsed.reason !== 'string' ||
      typeof parsed.occurredAt !== 'string'
    ) {
      return null
    }

    return parsed as BillingChangedEvent
  } catch {
    return null
  }
}

const isUnauthorizedAuthError = (error: unknown) =>
  error instanceof ApiError && (error.status === 401 || error.status === 403)

export function CatalogFreshnessBootstrap() {
  const userId = useUserStore((state) => state.user?.id ?? null)
  const setUser = useUserStore((state) => state.setUser)
  const clearUser = useUserStore((state) => state.clearUser)

  useEffect(() => {
    if (!userId) {
      return
    }

    let disposed = false
    let stream: EventSource | null = null
    let reconnectTimer: number | null = null
    let lastAccessVersion: string | null = null
    let lastBillingVersion: string | null = null

    const syncAuthContext = async (
      source: 'bootstrap' | 'poll' | 'focus' | 'catalog_event' | 'billing_event'
    ) => {
      try {
        const response = await authApi.me()
        setUser(response.user)
        const nextAccessVersion = response.user.accessVersion ?? null
        const hasVersionChanged = nextAccessVersion !== lastAccessVersion
        lastAccessVersion = nextAccessVersion

        if ((source === 'poll' || source === 'focus') && hasVersionChanged) {
          await invalidateCatalogQueries()
        }
      } catch (error) {
        if (isUnauthorizedAuthError(error)) {
          clearUser()
          broadcastAuthEvent('logout')
        }
      }
    }

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer !== null) {
        return
      }

      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null
        connectStream()
      }, RECONNECT_DELAY_MS)
    }

    const connectStream = () => {
      if (disposed) {
        return
      }

      stream?.close()
      stream = new EventSource(buildStreamUrl(), { withCredentials: true })

      stream.addEventListener('catalog_access_changed', (event) => {
        if (disposed || !(event instanceof MessageEvent)) {
          return
        }

        const payload = parseCatalogAccessChangedEvent(event.data)
        if (!payload || payload.userId !== userId || payload.accessVersion === lastAccessVersion) {
          return
        }

        lastAccessVersion = payload.accessVersion
        void (async () => {
          await invalidateCatalogQueries()
          await syncAuthContext('catalog_event')
        })()
      })

      stream.addEventListener('billing_changed', (event) => {
        if (disposed || !(event instanceof MessageEvent)) {
          return
        }

        const payload = parseBillingChangedEvent(event.data)
        if (
          !payload ||
          payload.userId !== userId ||
          payload.billingVersion === lastBillingVersion
        ) {
          return
        }

        if (payload.reason === 'initial_sync' && lastBillingVersion === null) {
          lastBillingVersion = payload.billingVersion
          return
        }

        lastBillingVersion = payload.billingVersion
        void (async () => {
          await invalidateBillingQueries()
          await syncAuthContext('billing_event')
        })()
      })

      stream.addEventListener('error', () => {
        stream?.close()
        scheduleReconnect()
      })
    }

    void syncAuthContext('bootstrap')
    connectStream()

    const pollTimer = window.setInterval(() => {
      void syncAuthContext('poll')
    }, POLL_INTERVAL_MS)

    const handleFocus = () => {
      void syncAuthContext('focus')
    }
    window.addEventListener('focus', handleFocus)

    return () => {
      disposed = true
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer)
      }
      window.clearInterval(pollTimer)
      window.removeEventListener('focus', handleFocus)
      stream?.close()
    }
  }, [clearUser, setUser, userId])

  return null
}
